import { algoliasearch } from "algoliasearch";
import { find, flattenDeep, has, isNil, isObject, memoize } from "lodash-es";
import { makeAutoObservable, runInAction } from "mobx";
import { apiClient } from "../../common/apiClient";
import { ENV_NAME, STATICDATA_HOST } from "../../common/environment";
import { PageNotFoundError } from "../../common/errors";
import { logger } from "../../common/logger";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { CategoryContext, CategoryInfo, CategoryQuery } from "../../common/types/categoryTypes";
import { keepIdOnly } from "../../common/utils/segmentUtils";
import theme from "../../themes/theme";
import authStore from "../auth/authStore";
import featuresStore from "../features/featuresStore";
import productStore from "../product/productStore";
import uiStore from "../uiStore";
import {
   findCategory,
   searchCategoryRescursive,
   searchForCategoryProducts,
   setHiddenStatus,
   updateSkusForCategory
} from "./categoryUtils";

type CategoryStoreType = BaseStoreType & {
   menuCategories: CategoryInfo[];
   navigationCategories: CategoryInfo[];
   categories: CategoryInfo[];
   leftMenuOn: boolean;
   mobile: {
      visible: boolean;
      path: string[];
   };
   showMenu(setTo: boolean): void;
   findCategoryByUrlKey(urlKeys: CategoryQuery): CategoryContext | null;
   findCategoryKeysBySku(sku: string): CategoryQuery[];
   hideEmptyCategories(): void;
   addNavigationCategory(urlKeys: CategoryQuery, name: string, index: number, skus: string[]): void;
   fillStore(): Promise<void>;
   updateCategorySkus(): Promise<void>;
};

const searchClient = algoliasearch(theme.searchConfig.algoliaConfig.appId, theme.searchConfig.algoliaConfig.searchApiKey);

const { categorySearchIndexName } = theme.searchConfig;

const categoryStore: CategoryStoreType = makeAutoObservable({
   menuCategories: [],
   navigationCategories: [],

   leftMenuOn: false,
   mobile: {
      visible: false,
      path: []
   },

   get categories() {
      return theme.storeId !== "tinehandel" || featuresStore.navigationTestVariant === "megamenu"
         ? this.menuCategories
         : this.navigationCategories;
   },

   // Used for the mobile menu that slides in
   showMenu: (setTo) => {
      categoryStore.mobile.visible = setTo;
   },
   addNavigationCategory: (urlKeys, name, index, skus) => {
      const { cat1, cat2, cat3 } = urlKeys;
      const existingCategory = findCategory(urlKeys, categoryStore.navigationCategories);
      if (existingCategory) {
         existingCategory.skus = skus;
         return;
      }
      const parentKeys = cat3 ? { cat1, cat2 } : { cat1 };
      const key = cat3 || cat2;
      const id = [cat1, cat2, cat3].filter((key) => key).join("/");
      if (key) {
         const category: CategoryInfo = {
            id,
            catId: id,
            name,
            url_key: key,
            fullUrl: `/${id}.html`,
            children: [],
            skus
         };
         const parent = findCategory(parentKeys, categoryStore.navigationCategories);
         if (parent) {
            parent.children.splice(index, 0, category);
            categoryStore.navigationCategories = [...categoryStore.navigationCategories];
         }
      }
   },

   updateCategorySkus: async () => {
      if (categorySearchIndexName) {
         const assortment = productStore.assortment;
         const replacementProducts = productStore.replacementProducts;
         const company = authStore.getCurrentCompany();
         const customerGroup = company ? keepIdOnly(company.customerGroup) : "default";

         try {
            const skusAndPaths = await searchForCategoryProducts(
               assortment,
               replacementProducts,
               searchClient,
               categorySearchIndexName,
               customerGroup
            );
            runInAction(() => {
               for (const category of categoryStore.navigationCategories) {
                  updateSkusForCategory(category, skusAndPaths, productStore.newProductSkus);
               }
               for (const category of categoryStore.menuCategories) {
                  updateSkusForCategory(category, skusAndPaths, productStore.newProductSkus);
               }
            });

            logger.log("categories", `Found ${skusAndPaths.length} skus for categories`);
         } catch (error) {
            logger.error("categories", "Error updating category skus:", error);
         }
      }
   },

   findCategoryByUrlKey: (urlKeys) => {
      const levels: CategoryInfo[] = [];
      let category = find(categoryStore.categories, { url_key: urlKeys.cat1 });
      if (isNil(category)) {
         throw new PageNotFoundError();
      }
      levels.push(category);

      if (has(urlKeys, "cat2")) {
         const category2 = find(category.children, { url_key: urlKeys.cat2 });
         if (!isNil(category2)) {
            category = category2;
            levels.push(category2);
         } else {
            throw new PageNotFoundError();
         }
      }

      if (has(urlKeys, "cat3")) {
         const category3 = find(category.children, { url_key: urlKeys.cat3 });
         if (!isNil(category3)) {
            category = category3;
            levels.push(category3);
         } else {
            throw new PageNotFoundError();
         }
      }

      return {
         category,
         levels
      };
   },

   findCategoryKeysBySku: memoize((lookForSku) => {
      return flattenDeep(categoryStore.categories.map((cat) => searchCategoryRescursive(lookForSku, cat, 1))).filter(isObject);
   }),

   hideEmptyCategories: () => {
      for (let i = 0; i < categoryStore.categories.length; i++) {
         setHiddenStatus(categoryStore.categories[i]);
      }
   },

   fillStore: async () => {
      const categoryDataUrl = `${STATICDATA_HOST}/categories.${ENV_NAME}_${uiStore.dataVersion}.json`;
      const navigationCategoryDataUrl = `${STATICDATA_HOST}/navigation-categories.${ENV_NAME}_${uiStore.dataVersion}.json`;
      try {
         await apiClient(categoryDataUrl)
            .get()
            .json((res) => {
               runInAction(() => {
                  categoryStore.menuCategories = res;
               });
               logger.log("categories", `Menu Categories loaded: ${categoryStore.menuCategories.length} root categories`);
            });
      } catch (error) {
         logger.error("categories", `Error loading menu categories: ${error}`);
      }

      if (theme.storeId === "tinehandel") {
         try {
            await apiClient(navigationCategoryDataUrl)
               .get()
               .json((res) => {
                  categoryStore.navigationCategories = res;
                  categoryStore.addNavigationCategory(
                     { cat1: "produkter", cat2: "nyheter" },
                     "Nyheter",
                     0,
                     productStore.newProductSkus
                  );

                  logger.log(
                     "categories",
                     `Navigation Categories loaded: ${categoryStore.navigationCategories.length} root categories`
                  );
               });
         } catch (error) {
            logger.error("categories", `Error loading navigation categories: ${error}`);
         }
      }
   },
   clearCompanySpecificData: () => {}
} satisfies CategoryStoreType);

export default categoryStore;
